import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { assignParticipantGroup } from "@/services/tokenService";
import type { Database } from "@/integrations/supabase/types";

type Survey = Database["public"]["Tables"]["surveys"]["Row"];
type SupportedLanguage = Database["public"]["Enums"]["supported_language"];
type ParticipantGroup = Database["public"]["Enums"]["participant_group"];

// Each visit has 3 survey parts. The total process has 3 visits (9 parts).
const PARTS_PER_VISIT = 3;
const TOTAL_PARTS = 9;

const SURVEY_IDS = {
  FIRST_SURVEY_ID: "c3d4e5f6-a7b8-9012-3456-cdefab123456", // Background
  BCAP_SURVEY_ID: "b2c3d4e5-f6a7-8901-2345-bcdefa123456", // BCAP
  LEISURE_ACTIVITIES_ID: "d4e5f6a7-b8c9-0123-4567-defabc123456", // Leisure
  SOCIAL_SERVICES_ID: "a1b2c3d4-e5f6-7890-1234-abcdef123456", // Trust
};

interface UseSurveyProps {
  token: string;
}

export const useSurvey = ({ token }: UseSurveyProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [participantGroup, setParticipantGroup] = useState<ParticipantGroup | null>(null);
  const [previousConsent, setPreviousConsent] = useState<{
    studyConsent: boolean;
    dataConsent: boolean;
    phoneNumber: string;
  } | null>(null);
  const [language, setLanguage] = useState<SupportedLanguage>("sv");
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [nextResponseNumber, setNextResponseNumber] = useState<number>(1);
  const [previousAnswers, setPreviousAnswers] = useState<Record<string, string> | null>(null);

  // Check for previous consent when component mounts
  useEffect(() => {
    if (token) {
      checkPreviousConsent(token);
    }
  }, [token]);

  const checkPreviousConsent = async (authToken: string) => {
    try {
      setIsLoading(true);
      // CRITICAL: Find the FIRST response with actual consent (not just most recent)
      // Some old responses may have been submitted without consent data
      const { data: responseData, error } = await supabase
        .from("survey_responses")
        .select("has_study_consent, has_data_consent, phone_number, language")
        .eq("auth_token", authToken)
        .eq("has_study_consent", true) // Only get responses where consent was given
        .order('created_at', { ascending: true }) // Get the FIRST consent
        .limit(1);
      
      if (error) throw error;
      
      if (responseData && responseData.length > 0) {
        console.log('=== PREVIOUS CONSENT LOADED ===');
        console.log('Query result:', responseData[0]);
        
        const consentData = {
          studyConsent: responseData[0].has_study_consent,
          dataConsent: responseData[0].has_data_consent,
          phoneNumber: responseData[0].phone_number || "",
        };
        
        console.log('Setting previousConsent to:', consentData);
        setPreviousConsent(consentData);
        
        // Set the previously used language if available
        if (responseData[0].language) {
          setLanguage(responseData[0].language as SupportedLanguage);
        }
        
        console.log("Found previous consent data:", responseData[0]);
        setIsLoading(false);
        return consentData; // Return consent data for immediate use
      }

      setIsLoading(false);
      return null;
    } catch (error) {
      console.error("Error fetching previous consent:", error);
      setIsLoading(false);
      return null;
    }
  };

  const handleValidToken = async (surveyData: Survey, completedSteps: number, nextRespNumber: number, currentLanguage?: SupportedLanguage) => {
    setIsLoading(true);
    console.log(`handleValidToken called with completedSteps: ${completedSteps}, nextResponseNumber: ${nextRespNumber}, language: ${currentLanguage || language}`);
    
    try {
      // CRITICAL: For follow-up surveys (completedSteps > 0), ensure we have previous consent loaded
      if (completedSteps > 0 && !previousConsent) {
        console.log('Follow-up survey detected, ensuring previous consent is loaded...');
        const loadedConsent = await checkPreviousConsent(token);
        if (!loadedConsent) {
          console.error('❌ CRITICAL: Could not load previous consent for follow-up survey');
          throw new Error('Previous consent data is required for follow-up surveys. Please contact support.');
        }
        console.log('✅ Previous consent loaded successfully:', loadedConsent);
      }
      // Get the group assignment from auth_tokens (only if already assigned)
      const { data: tokenData } = await supabase
        .from("auth_tokens")
        .select("assigned_group")
        .eq("token", token)
        .maybeSingle();
      
      if (tokenData?.assigned_group) {
        setParticipantGroup(tokenData.assigned_group);
      }
      
      // If all surveys are complete (nextRespNumber > 9), load the most recent answers for this survey
      if (nextRespNumber > 9) {
        console.log('Loading previous answers for completed survey');
        const { data: recentResponse } = await supabase
          .from("survey_responses")
          .select("answers")
          .eq("auth_token", token)
          .eq("survey_id", surveyData.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
          
        if (recentResponse?.answers) {
          setPreviousAnswers(recentResponse.answers as Record<string, string>);
          console.log('Loaded previous answers:', recentResponse.answers);
        }
      } else {
        // Check if there's already a response for this specific survey and response number
        console.log(`Checking for existing response for survey ${surveyData.id}, response number ${nextRespNumber}`);
        const { data: existingResponse } = await supabase
          .from("survey_responses")
          .select("answers")
          .eq("auth_token", token)
          .eq("survey_id", surveyData.id)
          .eq("response_number", nextRespNumber)
          .maybeSingle();
          
        if (existingResponse?.answers) {
          setPreviousAnswers(existingResponse.answers as Record<string, string>);
          console.log('Found existing response for this survey part:', existingResponse.answers);
        }
      }
      
      setSurvey(surveyData);
      setCurrentStep(completedSteps);
      setNextResponseNumber(nextRespNumber);
      
      // If a language is provided, update it
      if (currentLanguage) {
        setLanguage(currentLanguage);
      }
    } catch (error) {
      console.error("Error fetching participant group:", error);
      setSurvey(surveyData);
      setCurrentStep(completedSteps);
      setNextResponseNumber(nextRespNumber);

      if (currentLanguage) {
        setLanguage(currentLanguage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvalidToken = (error: string) => {
    setIsLoading(false);
    toast({
      title: "Invalid Token",
      description: error,
      variant: "destructive",
    });
    navigate("/");
  };

  // Get the correct survey ID based on the current step
  const getSurveyIdForStep = (step: number): string => {
    const visitNumber = Math.floor(step / PARTS_PER_VISIT);
    const partInVisit = step % PARTS_PER_VISIT;
    
    if (visitNumber === 0) {
      // First visit
      if (partInVisit === 0) return SURVEY_IDS.FIRST_SURVEY_ID; // Background
      if (partInVisit === 1) return SURVEY_IDS.BCAP_SURVEY_ID; // BCAP
      return SURVEY_IDS.SOCIAL_SERVICES_ID; // Trust
    } else {
      // Second and third visits
      if (partInVisit === 0) return SURVEY_IDS.LEISURE_ACTIVITIES_ID; // Leisure
      if (partInVisit === 1) return SURVEY_IDS.BCAP_SURVEY_ID; // BCAP
      return SURVEY_IDS.SOCIAL_SERVICES_ID; // Trust
    }
  };

  const syncToSavedProgress = async () => {
    const { data: progression, error } = await supabase
      .from("token_progression")
      .select("response_count")
      .eq("token", token)
      .maybeSingle();

    if (error) {
      throw error;
    }

    const responseCount = progression?.response_count ?? 0;
    const isFinalCompletion = responseCount >= TOTAL_PARTS;
    const isVisitComplete = responseCount > 0 && responseCount % PARTS_PER_VISIT === 0;

    console.log(`Syncing progress from DB. responseCount: ${responseCount}`);

    if (isFinalCompletion) {
      navigate("/survey-complete", {
        state: {
          participantGroup,
          token,
          language,
          waitingForNextVisit: false,
        },
      });
      return;
    }

    if (isVisitComplete) {
      const currentVisit = Math.floor(responseCount / PARTS_PER_VISIT);
      const nextVisit = currentVisit + 1;

      navigate("/survey-complete", {
        state: {
          participantGroup,
          token,
          language,
          waitingForNextVisit: true,
          currentVisit,
          nextVisit,
        },
      });
      return;
    }

    await loadNextSurveyPart(responseCount);
  };

  // Load the next survey part directly without relying on TokenValidator
  const loadNextSurveyPart = async (nextStep: number) => {
    try {
      setIsLoading(true);
      const nextSurveyId = getSurveyIdForStep(nextStep);
      
      console.log(`Loading next survey part: step ${nextStep}, survey ID: ${nextSurveyId}`);
      
      // Get the survey data
      const { data: surveyData, error: surveyError } = await supabase
        .from("surveys")
        .select("*")
        .eq("id", nextSurveyId)
        .maybeSingle();

      if (surveyError || !surveyData) {
        console.error('Error loading next survey:', surveyError);
        throw new Error("Failed to load next survey part");
      }

      // Clear previous answers for the new survey
      setPreviousAnswers(null);
      setSurvey(surveyData);
      setCurrentStep(nextStep);
      setNextResponseNumber(nextStep + 1);
      
      console.log(`Successfully loaded next survey part: ${surveyData.survey_type}`);
    } catch (error) {
      console.error("Error loading next survey part:", error);
      toast({
        title: "Error",
        description: "Failed to load next survey part. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (
    answers: Record<string, string>, 
    submittedLanguage: SupportedLanguage,
    phoneNumber?: string,
    responseTimeSeconds?: number,
    studyConsent?: boolean,
    dataConsent?: boolean
  ) => {
    console.log('useSurvey handleSubmit called with:', {
      answers,
      submittedLanguage,
      phoneNumber,
      responseTimeSeconds,
      studyConsent,
      dataConsent,
      token,
      surveyId: survey?.id,
      currentStep,
      nextResponseNumber
    });

    try {
      if (!token || !survey) {
        throw new Error('Missing token or survey data');
      }
      
      setIsLoading(true);

      // Update current language state with the submitted language
      setLanguage(submittedLanguage);
      console.log(`Submit started for token: ${token}, current step: ${currentStep}, using response_number: ${nextResponseNumber}, language: ${submittedLanguage}`);

      // Check if there's already a response for this specific survey and step
      const { data: existingStepResponse } = await supabase
        .from("survey_responses")
        .select("response_number, survey_id")
        .eq("auth_token", token)
        .eq("response_number", nextResponseNumber)
        .maybeSingle();
      
      if (existingStepResponse) {
        console.log(
          `Response already exists for token ${token}, response_number ${nextResponseNumber} (saved survey: ${existingStepResponse.survey_id}). Syncing to latest progress.`
        );
        await syncToSavedProgress();
        return;
      }
      
      console.log(`Submitting new response with response_number: ${nextResponseNumber}`);
      
      let assignedGroup = participantGroup;

      // Assign a group after completing the first survey (after response 3)
      if (nextResponseNumber === 3 && !assignedGroup && token) {
        try {
          assignedGroup = await assignParticipantGroup(token);
          setParticipantGroup(assignedGroup);
          console.log(`Assigned participant group: ${assignedGroup}`);
        } catch (error) {
          console.error("Failed to assign participant group:", error);
          // Continue with submission even if group assignment fails
        }
      }

      // Validate consent - parameters already contain the correct values
      // (form state initialized from previousConsent for follow-up surveys)
      console.log('=== CONSENT VALIDATION ===');
      console.log('currentStep:', currentStep);
      console.log('studyConsent received:', studyConsent);
      console.log('dataConsent received:', dataConsent);
      console.log('phoneNumber received:', phoneNumber);
      
      const finalStudyConsent = studyConsent ?? false;
      const finalDataConsent = dataConsent ?? false;
      
      console.log('Final consent values:');
      console.log('- finalStudyConsent:', finalStudyConsent);
      console.log('- finalDataConsent:', finalDataConsent);
      
      // Validate that consent exists
      if (!finalStudyConsent || !finalDataConsent) {
        console.error('❌ VALIDATION FAILED - Missing consent:', { 
          currentStep, 
          finalStudyConsent, 
          finalDataConsent 
        });
        throw new Error('Study and data consent are required. Please contact support.');
      }
      
      console.log('✅ Consent validation passed');

      // Prepare insert data
      const insertData = {
        survey_id: survey.id,
        auth_token: token,
        language: submittedLanguage,
        answers,
        phone_number: phoneNumber || previousConsent?.phoneNumber,
        response_number: nextResponseNumber,
        response_time_seconds: responseTimeSeconds || null,
        has_study_consent: finalStudyConsent,
        has_data_consent: finalDataConsent,
      };

      console.log('Inserting survey response:', insertData);

      // Insert response with consent information
      const { error: responseError } = await supabase
        .from("survey_responses")
        .insert(insertData);

      if (responseError) {
        console.error("Survey submission error:", responseError);
        console.error("Error details:", responseError?.details);
        console.error("Error message:", responseError?.message);
        console.error("Error code:", responseError?.code);
        throw responseError;
      }

      console.log(`Successfully submitted response with number: ${nextResponseNumber}`);

      // Update nextResponseNumber for the next survey
      setNextResponseNumber(nextResponseNumber + 1);

      // Calculate the next step
      const nextStep = currentStep + 1;
      console.log(`Updating currentStep from ${currentStep} to ${nextStep}`);

      // Check if we've completed the current visit (3 parts)
      const currentVisit = Math.floor(currentStep / PARTS_PER_VISIT) + 1;
      const isVisitComplete = nextStep % PARTS_PER_VISIT === 0;
      
      console.log(`Current visit: ${currentVisit}, isVisitComplete: ${isVisitComplete}, nextStep: ${nextStep}, nextStep % PARTS_PER_VISIT: ${nextStep % PARTS_PER_VISIT}`);
      
      if (nextStep >= TOTAL_PARTS) {
        // All surveys completed (3 visits of 3 parts each)
        console.log("All 9 survey parts completed");
        toast({
          title: submittedLanguage === 'sv' ? "Tack!" : "Thank you!",
          description: submittedLanguage === 'sv' 
            ? "Alla delar av enkäten har slutförts." 
            : "All survey parts have been completed.",
        });
        
        navigate("/survey-complete", { 
          state: { 
            participantGroup: assignedGroup || participantGroup,
            token,
            language: submittedLanguage,
            waitingForNextVisit: false
          } 
        });
      } else if (isVisitComplete) {
        // Visit complete, but more visits remain
        const nextVisit = currentVisit + 1;
        console.log(`Visit ${currentVisit} completed, next visit: ${nextVisit}`);
        toast({
          title: submittedLanguage === 'sv' ? "Besök slutfört!" : "Visit completed!",
          description: submittedLanguage === 'sv' 
            ? `Besök ${currentVisit} slutfört. Du kan återkomma vid nästa enkättillfälle.` 
            : `Visit ${currentVisit} completed. You can return at the next survey occasion.`,
        });
        
        navigate("/survey-complete", { 
          state: { 
            participantGroup: assignedGroup || participantGroup,
            token,
            language: submittedLanguage,
            waitingForNextVisit: true,
            currentVisit,
            nextVisit
          } 
        });
      } else {
        // More parts in current visit, load next survey directly
        const partInVisit = (currentStep % PARTS_PER_VISIT) + 1;
        console.log(`Part ${partInVisit}/${PARTS_PER_VISIT} of visit ${currentVisit} completed. Loading next part (step ${nextStep})...`);
        
        // Show progress toast
        toast({
          title: submittedLanguage === 'sv' ? "Del slutförd" : "Section completed",
          description: submittedLanguage === 'sv' 
            ? `Del ${partInVisit}/${PARTS_PER_VISIT} slutförd. Laddar nästa del...` 
            : `Section ${partInVisit}/${PARTS_PER_VISIT} completed. Loading next section...`,
        });

        // Load the next survey part directly
        await loadNextSurveyPart(nextStep);
      }
    } catch (error: any) {
      console.error("Error submitting survey:", error);
      console.error("Error details:", error?.details);
      console.error("Error message:", error?.message);
      console.error("Error code:", error?.code);
      console.error("Error hint:", error?.hint);
      
      // Log submission context for debugging
      console.error("Submission context:", {
        token,
        surveyId: survey?.id,
        nextResponseNumber,
        currentStep,
        hasStudyConsent: studyConsent,
        hasDataConsent: dataConsent
      });
      
      let errorMessage = "There was a problem submitting your response. Please try again.";
      
      // Provide more specific error messages based on the error
      if (error?.message?.includes('violates not-null constraint')) {
        errorMessage = "Please fill in all required fields before submitting.";
      } else if (error?.message?.includes('duplicate key value')) {
        console.log("Duplicate key detected, attempting to recover by loading next survey...");
        errorMessage = "This response has already been submitted. Loading next survey...";
        
        // Try to automatically recover by loading the next survey part
        try {
          await syncToSavedProgress();
          toast({
            title: "Moving to next survey",
            description: "This section was already completed.",
          });
          return; // Exit early if recovery successful
        } catch (recoveryError) {
          console.error("Failed to recover from duplicate:", recoveryError);
          errorMessage = "This response has already been submitted.";
        }
      } else if (error?.message) {
        errorMessage = `Submission failed: ${error.message}`;
      }
      
      toast({
        title: "Submission Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    survey,
    participantGroup,
    previousConsent,
    previousAnswers,
    language,
    isLoading,
    currentStep,
    totalSteps: PARTS_PER_VISIT,
    setLanguage,
    handleValidToken,
    handleInvalidToken,
    handleSubmit
  };
};
